import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PropertyPnlSnapshotPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [pnlReport, setPnlReport] = useState(null);
    const [pnlYear, setPnlYear] = useState(new Date().getFullYear());
    const [pnlLoading, setPnlLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value) || 0);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    const loadProperty = async () => {
        const propRes = await api.get('/property');
        if (propRes && propRes.data) {
            const currentProperty = propRes.data.find((p) => p.id === parseInt(propertyId, 10));
            setProperty(currentProperty || null);
        }
    };

    const loadPnlReport = async () => {
        if (!propertyId) return;
        setPnlLoading(true);
        setLoadError('');
        try {
            const pnlRes = await api.get(`/report/pnl/${propertyId}?year=${pnlYear}`);
            if (pnlRes && pnlRes.data) {
                setPnlReport(pnlRes.data);
            } else {
                setPnlReport(null);
            }
        } catch (e) {
            console.error('failed to load property pnl report', e);
            setPnlReport(null);
            setLoadError('Unable to load P&L data for this property and year.');
        } finally {
            setPnlLoading(false);
        }
    };

    const hasMonths = Array.isArray(pnlReport?.months) && pnlReport.months.length > 0;

    useEffect(() => {
        if (propertyId) {
            loadProperty();
            loadPnlReport();
        }
    }, [propertyId, pnlYear]);

    return (
        <div className="property-pnl-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>
                    ← Back to Property Details
                </button>
                <div className="property-pnl-title-block">
                    <h2>{property?.name || 'Property'} P&L Snapshot</h2>
                    <p>{property ? `${property.address1}, ${property.city}, ${property.state}` : 'Loading property...'}</p>
                </div>
                <div className="pnl-controls">
                    <label htmlFor="pnlYear">Year</label>
                    <input
                        id="pnlYear"
                        type="number"
                        value={pnlYear}
                        onChange={(e) => setPnlYear(e.target.value)}
                    />
                    <button type="button" className="btn-primary" onClick={loadPnlReport}>Reload</button>
                </div>
            </div>

            {pnlLoading && (
                <div className="app-state-card app-state-info" role="status" aria-live="polite">
                    <p>Loading P&L report...</p>
                </div>
            )}

            {!pnlLoading && loadError && (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={loadPnlReport}>Retry</button>
                    </div>
                </div>
            )}

            {!pnlLoading && !loadError && !hasMonths && (
                <div className="app-state-card">
                    <p>No P&L report data is available for this property and year.</p>
                </div>
            )}

            {!pnlLoading && !loadError && hasMonths && (
                <div className="property-pnl-card">
                    <div className="property-pnl-table-wrap">
                        <table className="pnl-table property-pnl-table">
                            <thead>
                                <tr>
                                    <th>Month</th>
                                    <th>Bookings</th>
                                    <th>Revenue</th>
                                    <th>Rent</th>
                                    <th>WiFi</th>
                                    <th>Supplies</th>
                                    <th>Other</th>
                                    <th>Pricing Tools</th>
                                    <th>Cleaning</th>
                                    <th>Water</th>
                                    <th>Gas</th>
                                    <th>Energy</th>
                                    <th>Unexpected OpEx</th>
                                    <th>Total Expense</th>
                                    <th>Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pnlReport.months.map((m) => (
                                    <tr key={m.month}>
                                        <td>{MONTH_LABELS[m.month - 1]}</td>
                                        <td>{m.bookingCount}</td>
                                        <td>{formatCurrency(m.revenue)}</td>
                                        <td>{formatCurrency(m.rentExpense)}</td>
                                        <td>{formatCurrency(m.wifiExpense)}</td>
                                        <td>{formatCurrency(m.suppliesExpense)}</td>
                                        <td>{formatCurrency(m.otherExpense)}</td>
                                        <td>{formatCurrency(m.pricingToolsExpense)}</td>
                                        <td>{formatCurrency(m.cleaningExpense)}</td>
                                        <td>{formatCurrency(m.waterExpense)}</td>
                                        <td>{formatCurrency(m.gasExpense)}</td>
                                        <td>{formatCurrency(m.energyExpense)}</td>
                                        <td>{formatCurrency(m.unexpectedOpExExpense)}</td>
                                        <td>{formatCurrency(m.expense)}</td>
                                        <td className={m.net >= 0 ? 'net-positive' : 'net-negative'}>{formatCurrency(m.net)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyPnlSnapshotPage;
